<template>
  <div>
    <SnapshotGallery
      name="Matthew Hoare"
      :images="galleryImages"
      @submit="gallerySelectionSubmitted"
      @notMe="notMeClick"
      v-if="state == 'gallery'"
    />
    <ConsentForm
      v-if="state == 'consent'"
      :privacyUrl="ucdPrivacyUrl"
      @accepted="handleConsentAccepted"
    />
    <SnapshotDetailsForm
      v-if="state == 'one'"
      :events="events"
      @submit="submitStepOne"
    />
    <SearchByFace
      v-if="state == 'face'"
      :name="personalDetails.name"
      :searching="searchInProgress"
      :search-error="searchError"
      :search-no-results="searchNoResults"
      @search-face="searchFace"
      @skip="state = 'gallery'"
    />
    <SnapshotImageSelector
      v-if="state == 'selector'"
      ref="imageSelector"
      :name="personalDetails.name"
      :images="images"
      :basketUrl="shareBasketdata"
      :is-free-digital="isFreeDigital"
      :free-download-success="freeDownloadSuccess"
      :free-download-error="freeDownloadError"
      :free-download-in-progress="freeDownloadInProgress"
      :last-order-nano-id="lastOrderNanoId"
      @checkout="submitImageSelection"
      @share="shareBasket"
      @retry-free-download="retryFreeDownload"
    />
    <!-- Product types disabled (digital-only) -->
    <!--
+    <ProductType 
+      v-if="state == 'type'"
+      @submit="submitProductType"
+    />
+    -->
    <CeremonyNotReady v-if="state == 'error'" />
    <BackButton v-if="isGoBackStatePossible" @goBack="goBackState" />
  </div>
</template>
  
  <script>
import { trackEvent } from "../../Analytics.vue";
import SnapshotImageSelector from "./SnapshotImageSelector.vue";
// Import stylesheet
import axios from "axios";
import "vue-loading-overlay/dist/vue-loading.css";
import SnapshotDetailsForm from "./SnapshotDetailsForm.vue";
import SnapshotGallery from "./SnapshotGallery.vue";
import CeremonyNotReady from "./CeremonyNotReady.vue";
import SearchByFace from "./SearchByFace.vue";
import BackButton from "./BackButton.vue";
import ConsentForm from "./ConsentForm.vue";
//import ProductType from "./ProductType.vue";
export default {
  name: "Snapshot",
  props: {
    organiser: Object,
    additionalFieldsInput: Array,
  },
  components: {
    SnapshotImageSelector,
    SnapshotDetailsForm,
    SnapshotGallery,
    CeremonyNotReady,
    SearchByFace,
    BackButton,
    ConsentForm,
    //ProductType
  },
  data: function () {
    return {
      state: "gallery",
      personalDetails: {},
      allImages: [],
      isLoading: false,
      searchInProgress: false,
      searchError: null,
      searchNoResults: false,
      events: [],
      registrationID: "",
      images: [],
      galleryImages: [],
      stepStack: [], // <— simple in-memory back stack
      showingPreviousNext: false,
      shareBasketdata: "",

      freeDownloadSuccess: false,
      freeDownloadError: null,
      freeDownloadInProgress: false,
      lastOrderNanoId: "",
      pendingState: null,
    };
  },
  mounted() {
    this.events = this.organiser["ceremonies"];
    // Seed the browser history with the initial step (no URL change)
    if (window?.history?.replaceState) {
      window.history.replaceState(
        { step: this.state },
        "",
        window.location.href
      );
    }
    window.addEventListener("popstate", this.onPopState);
  },
  beforeDestroy() {
    window.removeEventListener("popstate", this.onPopState);
  },
  computed: {
    ucdPrivacyUrl() {
      return this.requiresConsent
        ? 'https://www.ucd.ie/alumni/development-and-alumni-relations-privacy-policy/'
        : '';
    },
    requiresConsent() {
      // UCD headshots distribution requires consent gate
      const slug = this.$route.params.uni || "";
      return slug === "ucd" || slug === "ucd-test";
    },
    isFreeDigital() {
      // Example: depends on organiser config and order state
      if (this.$route.params.uni === "ucd-test" || this.$route.params.uni === "ucd") {
        return true;
      }
      if (this.lastOrderNanoId && this.freeDownloadSuccess) {
        return true;
      }
      return false;
    },
    // Show back only when we’ve actually moved forward inside the flow
    isGoBackStatePossible() {
      return (
        this.stepStack.length > 0 &&
        this.state !== "one" &&
        this.state !== "error"
      );
    },
  },
  created: function () {
    this.initialiseFlow();
  },
  methods: {
    consentStorageKey() {
      return "consent:ucd-headshots:v1";
    },
    hasAcceptedConsent() {
      if (typeof window !== 'undefined' && window.localStorage) {
        return window.localStorage.getItem(this.consentStorageKey()) === 'true';
      }
      return false;
    },
    markConsentAccepted() {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(this.consentStorageKey(), 'true');
      }
    },
    handleConsentAccepted() {
      this.markConsentAccepted();
      // Continue to the intended state if set, otherwise resume normal flow
      const next = this.pendingState;
      this.pendingState = null;
      try {
        trackEvent(this.$route.path, {
          label: "SNAPSHOT_CONSENT_ACCEPTED",
          university: this.$route.params.uni,
        });
      } catch (_) {console.log("Tracking consent acceptance failed");}
      if (next) {
        this.updateState(next, true);
      } else {
        this.initialiseFlow();
      }
    },
    gotoFaceStep(pushHistory = true) {
      if (this.requiresConsent && !this.hasAcceptedConsent()) {
        // Defer entering the selfie step until consent is accepted
        this.pendingState = "face";
        this.updateState("consent", pushHistory);
        try {
          trackEvent(this.$route.path, {
            label: "SNAPSHOT_CONSENT_SHOWN",
            reason: "BEFORE_SELFIE",
            university: this.$route.params.uni,
          });
        } catch (_) {console.log("Tracking consent display failed");}
        return;
      }
      try {
        trackEvent(this.$route.path, {
          label: "SNAPSHOT_CONSENT_ALREADY_ACCEPTED",
          step: "FACE",
          university: this.$route.params.uni,
        });
      } catch (_) {console.log("Tracking consent acceptance failed");}
      this.updateState("face", pushHistory);
    },
    initialiseFlow() {
      const share = this.$route.query.shareBasket;
      if (share) {
        this.receiveSharedBasket();
        // Initialisation → don’t push history yet
        this.updateState("selector", false);
        return;
      }
      var sub = this.$route.query.registration;
      if (typeof sub == "undefined") {
        // Initialisation → don’t push history yet
        this.updateState("one", false);
        return;
      }
      axios
        .get(`/registrations/${this.$route.query.registration}`)
        .then((r) => {
          this.registrationID = this.$route.query.registration;
          this.personalDetails.name = r.data.name;
          this.personalDetails.email = r.data.email;
          this.personalDetails.event = r.data.ceremony;
          this.resetFreeDownloadState(true);
          this.populated = true;

          // Initialisation → don’t push history yet
          this.updateState("selector", false);
        })
        .catch(() => {
          this.updateState("one", false);
        })
        .then(() => {
          if (!this.personalDetails?.event || !this.personalDetails?.name) return;
          return axios
            .post(`${process.env.VUE_APP_SNAPSHOT_API_BASE}/images`, {
              ceremony: this.personalDetails.event,
              name: this.personalDetails.name,
            })
            .then((r) => {
              console.log(r.data);
              this.allImages = r.data;
              this.galleryImages = r.data.map((x) => {
                return { src: x["preview"] };
              });
              if (r.data.length > 1 && r.data[0].score == r.data[1].score) {
                this.updateState("gallery", false);
              } else if (r.data.length == 1 || r.data[0].score > 0.75) {
                this.gallerySelectionSubmitted(0);
              } else {
                this.updateState("gallery", false);
              }
            })
            .catch(() => {
              this.updateState("error", false);
              trackEvent(this.$route.path, {
                label: "SNAPSHOT_NOT_READY",
              });
            });
        });
    },
    onPopState(e) {
      // Sync our local state when the user hits the browser back button
      if (this.stepStack.length) this.stepStack.pop();
      const step = e.state && e.state.step;
      const allowed = ["gallery", "one", "face", "selector", "consent", "error"];
      if (allowed.includes(step)) this.state = step;
    },
    resetFreeDownloadState(resetFreeFlag = false) {
      if (resetFreeFlag) {
        this.isFreeDigital = false;
      }
      this.freeDownloadSuccess = false;
      this.freeDownloadError = null;
      this.freeDownloadInProgress = false;
      this.lastOrderNanoId = "";
    },
    firstname(name) {
      return name.split(" ")[0];
    },
    searchFace(dataUrl) {
      // Reset any prior results/errors and display a clear loading state
      this.searchError = null;
      this.searchNoResults = false;
      this.searchInProgress = true;
      this.images = [];
      var printOnly = false;
      if (typeof this.$route.query.printOnly === undefined) {
        printOnly = false;
      } else {
        if (this.$route.query.printOnly == "true") {
          printOnly = true;
        } else {
          printOnly = false;
        }
      }
      axios
        .post(`${process.env.VUE_APP_SNAPSHOT_API_BASE}/search`, {
          ceremony_id: this.personalDetails.event,
          image: dataUrl,
          exclude_landscape: printOnly,
        })
        .then((r) => {
          this.searchInProgress = false;
          console.log(r.data);
          if (!Array.isArray(r.data) || r.data.length === 0) {
            // Stay on face step and show a gentle no-results banner
            this.searchNoResults = true;
            return;
          }
          // if(this.displayProductType(this.events)){
          //   this.updateState('type', true);
          // } else {
          //   this.updateState('selector', true);
          // }
          // Digital only: skip product-type step
          this.images = r.data.map((x) => {
            return { src: x };
          });
          this.updateState("selector", true); // user action → push history
        })
        .catch(() => {
          // Keep user on face step, clear any stale selector images, and show error
          this.searchInProgress = false;
          this.images = [];
          this.searchError = "We had trouble searching for your photos. Please try again or skip to browse.";
        });
    },
    submitStepOne(formData) {
      const previousEvent = this.personalDetails?.event;
      this.personalDetails = formData;
      const ceremonyChanged = previousEvent !== this.personalDetails.event;
      this.resetFreeDownloadState(ceremonyChanged);
      trackEvent(this.$route.path, {
        label: "SNAPSHOT_DETAILS_FORM_SUBMITTED",
        ceremony: this.personalDetails.event,
      });
      if (
        this.$route.params.uni === "adelaide" ||
        this.$route.params.uni === "bruford" ||
        this.$route.params.uni === "torrens-test" ||
        this.$route.params.uni === "ucd-test" ||
        this.$route.params.uni === "ucd" ||
        this.$route.params.uni === 'gfpevents-test-uni'
      ) {
        // THIS WAS BREAKING BIFFS CHANGES
        console.log("Skipping to face search for Adelaide but not really");
        // Intercept with consent if required before entering selfie step
        this.gotoFaceStep(true);
        // trackEvent(this.$route.path, {
        //   label: "SNAPSHOT_SKIP_TO_FACE",
        //   university: "adelaide"
        // });
        return;
      }
      axios
        .post(`${process.env.VUE_APP_SNAPSHOT_API_BASE}/images`, {
          ceremony: this.personalDetails.event,
          name: this.personalDetails.name,
          email: this.personalDetails.email,
        })
        .then((r) => {
          console.log(r.data);
          this.allImages = r.data;
          this.galleryImages = r.data.map((x) => {
            return { src: x["preview"] };
          });
          if (r.data.length > 1 && r.data[0].score == r.data[1].score) {
            this.gotoFaceStep(true);

            //this.state = "gallery"
          } else if (r.data.length == 1 || r.data[0].score > 0.75) {
            this.gotoFaceStep(true);
            //this.gallerySelectionSubmitted(0);
          } else {
            this.gotoFaceStep(true);
            //this.state = "gallery";
          }

          const [minScoreVal, maxScoreVal] = this.calculateScoreRanges(r.data);
          let minScore = minScoreVal;
          let maxScore = maxScoreVal;
          console.log("Score range:", minScore, "to", maxScore);

          const [minImagesVal, maxImagesVal, totalImages] =
            this.calculateImageRanges(r.data);
          let minImages = minImagesVal;
          let maxImages = maxImagesVal;
          const meanImages =
            r.data.length > 0 ? totalImages / r.data.length : 0;
          minImages = minImages === Infinity ? 0 : minImages;

          trackEvent(this.$route.path, {
            label: "SNAPSHOT_IMAGES_FROM_TRANSCRIPT_RESPONSE",
            API_endpoint: "/images",
            image_groups_found: r.data.length,
            score_ranges: {
              min_score: minScore,
              max_score: maxScore,
            },
            image_ranges: {
              min_images: minImages,
              max_images: maxImages,
              mean_images: meanImages.toFixed(2),
            },
          });
        })
        .catch(() => {
          this.updateState("error", true);
        });
    },
    calculateScoreRanges(data) {
      // Calculate min and max scores of groups returned
      // From /images request
      let minScore = 0;
      let maxScore = 0;
      if (data.length > 0) {
        let scores = data
          .map((item) => item.score)
          .filter((score) => score !== undefined);
        minScore = Math.min(...scores);
        maxScore = Math.max(...scores);
      }
      return [minScore, maxScore];
    },
    calculateImageRanges(data) {
      // Calculate ranges of images per group returned
      // From /images request
      let totalImages = 0;
      let minImages = Infinity;
      let maxImages = 0;

      data.forEach((item) => {
        if (item.images && Array.isArray(item.images)) {
          const imageCount = item.images.length;
          console.log("Images in group:", imageCount);
          totalImages += imageCount;
          minImages = Math.min(minImages, imageCount);
          maxImages = Math.max(maxImages, imageCount);
        }
      });
      return [minImages, maxImages, totalImages];
    },
    notMeClick() {
      console.log("Thats not me - showing previous and next");
      trackEvent(this.$route.path, {
        label: "SNAPSHOT_NOT_ME",
      });
      var newImages = [];
      this.allImages.forEach((element) => {
        if (element.previous != null) {
          newImages.push(element.previous);
        }
        if (element.next != null) {
          newImages.push(element.next);
        }
      });
      console.log(newImages);
      this.allImages = newImages;
      this.showingPreviousNext = true;
      this.galleryImages = newImages.map((x) => {
        return { src: x["preview"] };
      });
      console.log(this.galleryImages);
    },
    submitImageSelection(items, form) {
      console.log(items);
      if (form) {
        console.log(form);
        this.personalDetails["name"] = form["name"];
        this.personalDetails["email"] = form["email"];
      }
      this.resetFreeDownloadState();
      axios
        .post(`${process.env.VUE_APP_SNAPSHOT_API_BASE}/orders`, {
          name: this.personalDetails["name"],
          email: this.personalDetails["email"],
          ceremony: this.personalDetails["event"],
          slug: this.$route.params.uni,
          tenant: "mygraduationfilm",
          items: items,
        })
        .then((r) => {
          trackEvent(this.$route.path, {
            label: "SNAPSHOT_SUBMIT_ORDER_RESPONSE",
          });
          const data = r.data;
          if (typeof data === "string") {
            window.location.href = data;
            return;
          }
          if (
            data &&
            Object.prototype.hasOwnProperty.call(data, "digitalFreeCeremony")
          ) {
            this.isFreeDigital = Boolean(data.digitalFreeCeremony);
          }
          const redirectUrl = data?.redirectUrl;
          const freeOrder = data?.freeOrder;
          const orderNanoId = data?.orderNanoId;
          if (freeOrder) {
            trackEvent(this.$route.path, {
              label: "SNAPSHOT_FREE_ORDER_CREATED",
              orderNanoId: orderNanoId,
            });
            this.lastOrderNanoId = orderNanoId || "";
            this.triggerFreeDownload(orderNanoId);
            return;
          }
          if (redirectUrl) {
            window.location.href = redirectUrl;
            return;
          }
          if (data?.url) {
            window.location.href = data.url;
            return;
          }
          console.warn("Unexpected order response", data);
        })
        .catch((error) => {
          console.error(error);
          alert(
            "Something went wrong while submitting your order. Please try again."
          );
        });
    },
    gallerySelectionSubmitted(index) {
      trackEvent(this.$route.path, {
        label: "SNAPSHOT_GALLERY_SELECTION_SUBMITTED",
        img_index: index,
        img_src: this.allImages[index]["preview"],
      });
      // Fix this
      this.images = this.allImages[index]["images"].map((x) => {
        return { src: x };
      });

      // if(this.displayProductType(this.events)){
      //   this.updateState("type", true);
      // } else {
      //   this.updateState("selector", true);
      // }
      // No URL change — keep internal state only
      this.updateState("selector", true);
    },
    updateState(newState, pushHistory = true) {
      if (pushHistory && window?.history?.pushState) {
        this.stepStack.push(this.state);
        // Push a history entry without changing the URL
        window.history.pushState({ step: newState }, "", window.location.href);
      }
      this.state = newState;
    },
    addStatetoQuery(newState) {
      const currentQuery = { ...this.$route.query };
      currentQuery.state = newState;
      // Force digital-only everywhere
      delete currentQuery.printOnly;
      currentQuery.digitalOnly = "true";
      this.$router
        .push({
          path: this.$route.path,
          query: currentQuery,
        })
        .catch((err) => {
          if (err.name !== "NavigationDuplicated") {
            throw err;
          }
        });
    },
    goBackState() {
      if (this.stepStack.length) {
        window.history.back(); // popstate listener will update `state` and stack
      }
    },
    shareBasket(items) {
      const imageEncode = this.encodeImagesForSharing(items);

      // Create url
      // TODO: Update domain
      let domain = window.location.hostname;
      if (domain === "localhost") {
        domain += `:${window.location.port}`;
      }
      // let url = `${domain}/snapshot/${this.$route.params.uni}?shareBasket=true&ceremony=${this.personalDetails["event"]}&items=${imageEncode}`;

      // if(this.$route.query.printOnly){
      //   const printOnly = '&printOnly=true';
      //   url+= printOnly;
      // }
      // Always digital-only for now (use full origin for QR reliability)
      const origin = window.location.origin;
      const url = `${origin}/snapshot/${this.$route.params.uni}?shareBasket=true&ceremony=${this.personalDetails["event"]}&items=${imageEncode}&digitalOnly=true`;

      this.shareBasketdata = url;
    },
    retryFreeDownload() {
      if (!this.lastOrderNanoId) {
        return;
      }
      this.triggerFreeDownload(this.lastOrderNanoId);
    },
    triggerFreeDownload(orderNanoId) {
      if (!orderNanoId) {
        this.freeDownloadError = "We couldn't find your order reference.";
        return;
      }
      this.freeDownloadSuccess = false;
      this.freeDownloadInProgress = true;
      this.freeDownloadError = null;
      trackEvent(this.$route.path, {
        label: "SNAPSHOT_FREE_ORDER_DOWNLOAD_START",
        orderNanoId: orderNanoId,
      });
      axios
        .post(`${process.env.VUE_APP_SNAPSHOT_API_BASE}/download-digitals`, {
          order_nano_id: orderNanoId,
        })
        .then((response) => {
          const downloadUrl = response?.data?.url;
          if (downloadUrl) {
            const newWindow = window.open(downloadUrl, "_blank");
            if (!newWindow) {
              window.location.href = downloadUrl;
            }
            this.freeDownloadSuccess = true;
            trackEvent(this.$route.path, {
              label: "SNAPSHOT_FREE_ORDER_DOWNLOAD_SUCCESS",
              orderNanoId: orderNanoId,
            });
            if (this.$refs?.imageSelector?.resetSelection) {
              this.$refs.imageSelector.resetSelection();
            }
            return;
          }
          this.freeDownloadError =
            "We couldn't generate your download link. Please try again.";
          trackEvent(this.$route.path, {
            label: "SNAPSHOT_FREE_ORDER_DOWNLOAD_FAILED",
            orderNanoId: orderNanoId,
            message: this.freeDownloadError,
          });
        })
        .catch((error) => {
          const message =
            error?.response?.data?.message ||
            "We couldn't prepare your download right now. Please try again.";
          this.freeDownloadError = message;
          trackEvent(this.$route.path, {
            label: "SNAPSHOT_FREE_ORDER_DOWNLOAD_FAILED",
            orderNanoId: orderNanoId,
            message: message,
          });
        })
        .finally(() => {
          this.freeDownloadInProgress = false;
        });
    },
    encodeImagesForSharing(items) {
      let encodedImages = items.map((item) => {
        const imgName = item.src.split("/").pop();
        return btoa(imgName);
      });
      let urlEncode = encodeURIComponent(JSON.stringify(encodedImages));
      return urlEncode;
    },
    receiveSharedBasket() {
      // Decode items
      let uriDecode = decodeURIComponent(this.$route.query.items);
      let itemsJson = JSON.parse(uriDecode);
      //const isPrintPresent = this.$route.query.printOnly ? true : false;
      //const isPrintPresent = false;
      const event = this.$route.query?.ceremony;
      // Create item objs
      let itemsRetrieved = itemsJson.map((item) => {
        const decodedSrc = `${
          process.env.VUE_APP_SNAPSHOT_CF_DISTRIBUTION_URL
        }/${event}/thumbnails/${atob(item)}`;
        return {
          src: decodedSrc,
          isDigitalSelected: true,
          isPrintSelected: false,
          printQuantity: 0,
        };
      });
      console.log(itemsRetrieved);
      this.images = itemsRetrieved;
      const previousEvent = this.personalDetails?.event;
      this.personalDetails["event"] = event;
      this.resetFreeDownloadState(previousEvent !== event);
    },
    submitProductType(type) {
      this.updateState("selector", true);
      const queryParams = { ...this.$route.query };
      switch (type) {
        case "print":
          delete queryParams.digitalOnly;
          queryParams.printOnly = "true";
          this.$router.push({ query: queryParams });
          break;
        case "digital":
          delete queryParams.printOnly;
          queryParams.digitalOnly = "true";
          this.$router.push({ query: queryParams });
          break;
      }
    },
    // displayProductType(events){
    //   // Do we need to display the product type select options
    //   const printing = this.$route.query.printOnly === 'true' ? true : false;
    //   const digitally = this.$route.query.digitalOnly === 'true' ? true : false;
    //   if(printing || digitally){
    //     // Method already set
    //     return false;
    //   }
    //   // Remove hardcoded date that was causing issues
    //   // events[0]['datetime'] = '2025-01-24 13:30:00';
    //   const today = new Date();
    //   today.setHours(0, 0, 0, 0);
    //   // TODO: Factor for timezones
    //   const totalToday = events.filter(e => {
    //     const eventDate = new Date(e.datetime);
    //     eventDate.setHours(0, 0, 0, 0);
    //     console.log(e)
    //     return eventDate.getTime() === today.getTime();
    //   });
    //   if(totalToday.length > 0){
    //     // Events on today
    //     return false;
    //   }
    //   // No events today
    //   return true;
    // },
    displayProductType() {
      // Hard-disable product type and force digitalOnly in URL
      const query = { ...this.$route.query };
      delete query.printOnly;
      if (query.digitalOnly !== "true") {
        query.digitalOnly = "true";
        this.$router.replace({ query }).catch((err) => {
          if (err.name !== "NavigationDuplicated") {
            throw err;
          }
        });
      }
      return false;
    },
  },
};
</script>
  
